import React, { useCallback, useState } from 'react';
import { View } from 'react-native';
import { useFocusEffect, useIsFocused } from '@react-navigation/core';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { isEmpty } from 'lodash';
import { useAppTranslation } from '../../../hooks/useAppTranslation';
import Layout from '../../../components/Layout';
import {
  AppNavigationProp,
  NavigationParamList,
  ScreenName,
  StackNavigationScreenProps,
} from '../../../navigation/types';
import { useAuth } from '../../../contexts/AuthContext/AuthContext';
import { HelpdeskCategories, Role, TopicHelpdeskIssueItem } from '../../../client/interfaces';
import {
  Button,
  FormInput,
  FormErrorNotification,
  Modal,
  HelpdeskIssueSimpleTable,
} from '../../../components';
import { useAdmin } from '../../../contexts/Admin/AdminContext';
import { useAdminHelpdesk } from '../../../contexts/Admin/AdminHelpdeskContext';
import { useMessage } from '../../../contexts/Messages/MessageContext';
import { isWeb } from '../../../utils/responsive';
import client from '../../../client/client';
import { cln } from '../../../utils/classnames';

interface OrganizationSchema {
  nameHu: string;
  nameEn: string;
}

const emptyFormValues: OrganizationSchema = {
  nameHu: '',
  nameEn: '',
};

const AdminHelpdeskOrganizationEditScreen: React.FC<
  StackNavigationScreenProps<NavigationParamList, ScreenName.AdminHelpdeskOrganizationEditScreen>
> = ({ navigation: nav, route }) => {
  const navigation = nav as AppNavigationProp;
  const { t } = useAppTranslation();
  const { me, hasRole } = useAuth();
  const { isBackModalOpen, setIsBackModalOpen } = useAdmin();
  const { refreshTopicsAndOrganizations } = useAdminHelpdesk();
  const isFocused = useIsFocused();

  const [initialFormValues, setInitialFormValues] = useState<OrganizationSchema>(emptyFormValues);
  const [helpdeskIssues, setHelpdeskIssues] = useState<TopicHelpdeskIssueItem[]>([]);
  const { setMessage } = useMessage();

  const isNewOrganization = !route?.params?.organizationId;
  const requiredTranslation = t('admin:common:required_field');
  const tooLongTranslation = (max) => t('admin:common:max_characters', { max });

  const duplicateTranslation = t('admin:helpdesk_organization_edit:name_already_exists');

  const organizationSchema = Yup.object().shape({
    nameHu: Yup.string().required(requiredTranslation).max(60, tooLongTranslation(60)),
    nameEn: Yup.string().required(requiredTranslation).max(60, tooLongTranslation(60)),
  });

  const onSubmit = async (
    values: OrganizationSchema,
    { setSubmitting, resetForm, setFieldError, setFieldTouched },
  ) => {
    try {
      if (isNewOrganization) {
        await client.createHelpdeskOrganization({ ...values, isActive: true });
      } else {
        await client.updateHelpdeskOrganization(route.params.organizationId, values);
      }

      setSubmitting(false);
      resetForm({ values: emptyFormValues });
      await refreshTopicsAndOrganizations();
      if (route?.params?.returnScreen === ScreenName.AdminHelpdeskIssueEditScreen) {
        navigation.navigate(ScreenName.AdminHelpdeskIssueEditScreen, {
          postId: route.params.returnPostId,
        });
      } else {
        navigation.navigate(ScreenName.AdminHelpdeskCategoriesScreen, {
          activeTab: HelpdeskCategories.organizations,
        });
      }
    } catch (e) {
      if (e.response?.data?.detail?.[0]?.msg === 'Names are not valid') {
        const loc = (e.response.data.detail[0].loc[1] as string).toLowerCase();
        if (loc.includes(values.nameHu.toLowerCase())) {
          setFieldError('nameHu', duplicateTranslation);
          setFieldTouched('nameHu', true, false);
        }
        if (loc.includes(values.nameEn.toLowerCase())) {
          setFieldError('nameEn', duplicateTranslation);
          setFieldTouched('nameEn', true, false);
        }
      } else {
        setMessage({ message: e.message, type: 'error' });
      }
    }
  };

  // Role guard
  useFocusEffect(
    useCallback(() => {
      if (me && !hasRole(Role.publisher)) {
        navigation.navigate(ScreenName.HomeScreen);
      }
    }, [me?.roleList]),
  );

  useFocusEffect(
    useCallback(() => {
      if (isWeb) {
        window.scrollTo({ top: 0, behavior: 'instant' });
      }

      const fetchOrganization = async () => {
        try {
          const organizationRes = await client.getAdminHelpdeskOrganization(
            route.params.organizationId,
          );
          setInitialFormValues({
            nameHu: organizationRes.nameHu,
            nameEn: organizationRes.nameEn,
          });
          setHelpdeskIssues(organizationRes.helpdeskIssueList ?? []);
        } catch (e) {
          setMessage({ message: e.message, type: 'error' });
          navigation.navigate(ScreenName.AdminHelpdeskCategoriesScreen, {
            activeTab: HelpdeskCategories.organizations,
          });
        }
      };

      if (route?.params?.organizationId) {
        fetchOrganization();
      } else {
        setInitialFormValues(emptyFormValues);
      }
    }, [route?.params?.organizationId]),
  );

  return (
    <Layout
      title={
        isNewOrganization
          ? t('admin:helpdesk_organization_edit:create_title')
          : t('admin:helpdesk_organization_edit:edit_title')
      }
    >
      <Formik
        initialValues={initialFormValues}
        validationSchema={organizationSchema}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({ handleSubmit, isSubmitting, resetForm, validateForm, setTouched, values, dirty }) => (
          <View className="flex-1">
            <FormErrorNotification />
            <View
              className={cln(
                'flex-1 flex-row gap-x-20 z-30',
                isNewOrganization || helpdeskIssues.length === 0 ? 'mb-10' : 'mb-0',
              )}
            >
              {/* Hungarian column */}
              <View className="flex-1">
                <FormInput
                  name="nameHu"
                  title={t('admin:helpdesk_organization_edit:title')}
                  placeholder={t('admin:common:max_characters', { max: 60 })}
                  required
                />
              </View>

              {/* English column */}
              <View className="flex-1">
                <FormInput
                  name="nameEn"
                  title={t('admin:helpdesk_organization_edit:title_en')}
                  placeholder={t('admin:common:max_characters', { max: 60 })}
                  required
                />
              </View>
            </View>
            {!isNewOrganization && (
              <HelpdeskIssueSimpleTable
                rows={helpdeskIssues}
                title={t('admin:helpdesk_organization_edit:issues_table_title')}
                emptyText={t('admin:helpdesk_organization_edit:issues_table_empty')}
              />
            )}
            <View className="self-end flex-row gap-x-6">
              <Button
                text={t('admin:common:exit_from_edit')}
                size="medium"
                variant="link"
                onPress={() => {
                  setIsBackModalOpen(true);
                }}
                disabled={isSubmitting}
              />
              <Button
                text={t('admin:common:save_changes')}
                size="medium"
                variant="fill"
                onPress={async () => {
                  const validationErrors = await validateForm(values);
                  if (isEmpty(validationErrors)) {
                    handleSubmit();
                  } else {
                    setTouched({ nameHu: true, nameEn: true });
                  }
                }}
                disabled={isSubmitting || !dirty}
              />
            </View>
            <Modal
              showModal={isBackModalOpen && isFocused}
              title={t('admin:common:exit_warning')}
              callback={() => {
                setIsBackModalOpen(false);
                resetForm({ values: emptyFormValues });
                if (route?.params?.returnScreen === ScreenName.AdminHelpdeskIssueEditScreen) {
                  navigation.navigate(ScreenName.AdminHelpdeskIssueEditScreen, {
                    postId: route.params.returnPostId,
                  });
                } else {
                  navigation.navigate(ScreenName.AdminHelpdeskCategoriesScreen, {
                    activeTab: HelpdeskCategories.organizations,
                  });
                }
              }}
              closeModal={() => {
                setIsBackModalOpen(false);
              }}
              yesText={t('admin:common:exit')}
              cancelText={t('admin:common:stay')}
              instantCallback={!dirty}
            />
          </View>
        )}
      </Formik>
    </Layout>
  );
};

export default AdminHelpdeskOrganizationEditScreen;
